import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import TeacherReportView from "../components/TeacherReportView";
import "../css/EducatorDashboard.css";

const API = process.env.REACT_APP_API_BASE || "https://beintelli.engaggio.com/handlers";

const EducatorDashboard = () => {
  const {
    fetchEducatorDashboard, educatorData, loading,
    fetchEducatorActivities, createEducatorActivity,
    fetchActivitySubmissions, gradeActivitySubmission,
    fetchTeacherReportData,
  } = useAuth();

  const [tab, setTab] = useState("learners");
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [learnerTab, setLearnerTab] = useState("overview");

  /* join institution */
  const [joinCode, setJoinCode] = useState("");
  const [joinMsg, setJoinMsg] = useState("");

  /* activities */
  const [activities, setActivities] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [submissions, setSubmissions] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);

  /* create activity form */
  const [showForm, setShowForm] = useState(false);
  const [actForm, setActForm] = useState({ title: "", description: "", due_date: "", learner_id: "" });
  const [actMsg, setActMsg] = useState("");

  /* grade */
  const [gradeInputs, setGradeInputs] = useState({});
  const [gradeMsg, setGradeMsg] = useState("");

  /* report */
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMsg, setReportMsg] = useState("");

  useEffect(() => { fetchEducatorDashboard(); }, []);

  const token = localStorage.getItem("token");

  const handleJoinInstitution = async () => {
    if (!joinCode.trim()) return setJoinMsg("Enter an institution code.");
    try {
      const res = await fetch(`${API}/user/institution/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ institution_code: joinCode.trim() }),
      });
      const data = await res.json();
      setJoinMsg(res.ok ? "✅ " + data.message : "❌ " + data.message);
      if (res.ok) { setJoinCode(""); fetchEducatorDashboard(); }
    } catch {
      setJoinMsg("❌ Request failed");
    }
  };

  const handleSelectActivity = async (act) => {
    setSelectedActivity(act);
    setSubmissions(null);
    setGradeMsg("");
    const data = await fetchActivitySubmissions(act.id);
    setSubmissions(Array.isArray(data) ? data : data?.submissions || []);
  };

  const handleCreateActivity = async () => {
    if (!actForm.title.trim()) return setActMsg("Title required.");
    setActMsg("");
    const res = await createEducatorActivity(actForm);
    if (res?.id || res?.message) {
      setActMsg("✅ Activity created!");
      setActForm({ title: "", description: "", due_date: "", learner_id: "" });
      setShowForm(false);
      const data = await fetchEducatorActivities();
      setActivities(Array.isArray(data) ? data : data?.activities || []);
    } else {
      setActMsg("❌ " + (res?.error || "Failed"));
    }
  };

  const handleGrade = async (subId) => {
    const { grade, feedback } = gradeInputs[subId] || {};
    if (!grade) return setGradeMsg("Grade enter చేయండి.");
    const res = await gradeActivitySubmission(subId, grade, feedback || "");
    setGradeMsg(res ? "✅ Graded!" : "❌ Failed.");
  };

  if (loading) return <div>Loading...</div>;

  const handleTab = async (t) => {
    setTab(t);
    if (t !== "activities") return;
    setTabLoading(true);
    try {
      if (!activities) {
        const data = await fetchEducatorActivities();
        setActivities(Array.isArray(data) ? data : data?.activities || []);
      }
    } finally { setTabLoading(false); }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    setReportMsg("");
    const data = await fetchTeacherReportData();
    if (data) {
      setReportData(data);
    } else {
      setReportMsg("❌ Could not generate report. Make sure learners have completed assessments.");
    }
    setReportLoading(false);
  };

  const handleDownloadReport = () => {
    if (reportData?.file_path) {
      const API = process.env.REACT_APP_API_BASE || "https://beintelli.engaggio.com/handlers";
      window.open(`${API.replace("/handlers", "")}${reportData.file_path}`, "_blank");
    }
  };

  const TABS = [
    { key: "learners",   label: "Learners" },
    { key: "activities", label: "Activities" },
    { key: "report",     label: "Class Report" },
  ];

  return (
    <div className="educator-dashboard">
      <header className="dashboard-header">
        <h1>Educator Dashboard</h1>
        <p>Manage learners, activities, and engagement</p>
        {educatorData?.institution_name && (
          <p style={{ fontSize: 13, color: "#64748b" }}>
            Institution: <strong>{educatorData.institution_name}</strong>
          </p>
        )}
      </header>

      {/* JOIN INSTITUTION */}
      {!educatorData?.institution_name && (
        <section className="card">
          <h2>Join an Institution</h2>
          <p style={s.hint}>Enter the code provided by your institution to link your account.</p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              style={s.input}
              placeholder="Institution code"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
            />
            <button style={s.btn} onClick={handleJoinInstitution}>Join</button>
          </div>
          {joinMsg && <p style={{ marginTop: 10, fontSize: 13 }}>{joinMsg}</p>}
        </section>
      )}

      {/* TABS */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0 16px" }}>
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleTab(key)}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: 13,
              background: tab === key ? "#1e293b" : "#f1f5f9",
              color: tab === key ? "#fff" : "#475569",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tabLoading && <div className="card">Loading...</div>}

      {/* LEARNERS TAB */}
      {tab === "learners" && !tabLoading && (
        <>
          <section className="card">
            <h2>Assigned Learners</h2>
            {educatorData?.learners?.length ? (
              <div className="learner-list">
                {educatorData.learners.map((learner) => (
                  <button
                    key={learner.id}
                    className={`learner-card ${selectedLearner?.id === learner.id ? "active" : ""}`}
                    onClick={() => { setSelectedLearner(learner); setLearnerTab("overview"); }}
                    style={{ cursor: "pointer", textAlign: "left", background: "none", border: "none", width: "100%" }}
                  >
                    <h3>{learner.full_name}</h3>
                    <p>Grade: {learner.grade || "—"}</p>
                    <p style={{ fontSize: 12, color: "#94a3b8" }}>Status: {learner.status || "—"}</p>
                  </button>
                ))}
              </div>
            ) : (
              <p style={s.hint}>No learners assigned yet.</p>
            )}
          </section>

          {selectedLearner && (
            <>
              {/* learner sub-tabs */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0 12px" }}>
                {["overview", "activities", "attendance", "reports"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setLearnerTab(t)}
                    style={{
                      padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                      fontWeight: 600, fontSize: 12,
                      background: learnerTab === t ? "#6366f1" : "#f1f5f9",
                      color: learnerTab === t ? "#fff" : "#475569",
                    }}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {learnerTab === "overview" && (
                <section className="card">
                  <h2>{selectedLearner.full_name} — Overview</h2>
                  <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                    <div style={s.statBox}><span style={s.statNum}>{selectedLearner.score ?? "—"}</span><span style={s.statLabel}>Score</span></div>
                    <div style={s.statBox}><span style={s.statNum}>{selectedLearner.status || "—"}</span><span style={s.statLabel}>Status</span></div>
                    <div style={s.statBox}><span style={s.statNum}>{selectedLearner.attendance?.present ?? "—"}</span><span style={s.statLabel}>Present</span></div>
                  </div>
                </section>
              )}

              {learnerTab === "activities" && (
                <section className="card">
                  <h2>Activities</h2>
                  {selectedLearner?.activities?.length ? (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {selectedLearner.activities.map((a, i) => (
                        <li key={a.id || i} style={{ padding: "10px 0", borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>
                          <strong>{a.title}</strong> — {a.status}
                          {a.grade != null && <span style={{ marginLeft: 10, color: "#6366f1", fontWeight: 600 }}>Grade: {a.grade}</span>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={s.hint}>No activities for this learner.</p>
                  )}
                </section>
              )}

              {learnerTab === "attendance" && (
                <section className="card">
                  <h2>Attendance</h2>
                  <div style={{ display: "flex", gap: 24 }}>
                    <div style={s.statBox}><span style={s.statNum}>{selectedLearner.attendance?.present ?? 0}</span><span style={s.statLabel}>Present</span></div>
                    <div style={s.statBox}><span style={s.statNum}>{selectedLearner.attendance?.absent ?? 0}</span><span style={s.statLabel}>Absent</span></div>
                  </div>
                </section>
              )}

              {learnerTab === "reports" && (
                <section className="card">
                  <h2>Reports</h2>
                  <p style={s.hint}>Reports are generated by the learner or institution.</p>
                </section>
              )}
            </>
          )}
        </>
      )}

      {/* REPORT TAB */}
      {tab === "report" && (
        <section className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>Class Report</h2>
            {!reportData && (
              <button style={s.btn} onClick={handleGenerateReport} disabled={reportLoading}>
                {reportLoading ? "Generating…" : "Generate Report"}
              </button>
            )}
            {reportData && (
              <button style={{ ...s.btn, background: "#64748b" }} onClick={() => setReportData(null)}>
                Regenerate
              </button>
            )}
          </div>
          {reportMsg && <p style={{ fontSize: 13, color: "#dc2626" }}>{reportMsg}</p>}
          {reportLoading && <p style={s.hint}>Generating AI class report… this may take a moment.</p>}
          {reportData && (
            <TeacherReportView
              data={reportData}
              onDownload={handleDownloadReport}
              downloadLoading={false}
            />
          )}
          {!reportData && !reportLoading && !reportMsg && (
            <p style={s.hint}>Click "Generate Report" to create a Gemini AI class engagement report for your learners.</p>
          )}
        </section>
      )}

      {/* ACTIVITIES TAB */}
      {tab === "activities" && !tabLoading && (
        <>
          {/* Activity list */}
          {!selectedActivity && (
            <section className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={{ margin: 0 }}>Activities</h2>
                <button style={s.btn} onClick={() => setShowForm(!showForm)}>
                  {showForm ? "Cancel" : "+ Create Activity"}
                </button>
              </div>

              {/* CREATE FORM */}
              {showForm && (
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: 16, marginBottom: 16, border: "1px solid #e2e8f0" }}>
                  <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>New Activity</h3>
                  <div style={s.formGrid}>
                    <input
                      style={s.input}
                      placeholder="Title *"
                      value={actForm.title}
                      onChange={(e) => setActForm({ ...actForm, title: e.target.value })}
                    />
                    <input
                      style={s.input}
                      type="date"
                      placeholder="Due date"
                      value={actForm.due_date}
                      onChange={(e) => setActForm({ ...actForm, due_date: e.target.value })}
                    />
                    <select
                      style={s.input}
                      value={actForm.learner_id}
                      onChange={(e) => setActForm({ ...actForm, learner_id: e.target.value })}
                    >
                      <option value="">— Assign to learner (optional) —</option>
                      {(educatorData?.learners || []).map((l) => (
                        <option key={l.id} value={l.id}>{l.full_name}</option>
                      ))}
                    </select>
                    <textarea
                      style={{ ...s.input, gridColumn: "1 / -1", minHeight: 70, resize: "vertical" }}
                      placeholder="Description"
                      value={actForm.description}
                      onChange={(e) => setActForm({ ...actForm, description: e.target.value })}
                    />
                  </div>
                  <button style={{ ...s.btn, marginTop: 12 }} onClick={handleCreateActivity}>
                    Create
                  </button>
                  {actMsg && <p style={{ marginTop: 8, fontSize: 13 }}>{actMsg}</p>}
                </div>
              )}

              {/* ACTIVITY LIST */}
              {activities?.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {activities.map((a) => (
                    <div key={a.id} style={s.actCard}>
                      <div>
                        <strong>{a.title}</strong>
                        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>{a.description || ""}</p>
                        {a.due_date && (
                          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>
                            Due: {new Date(a.due_date).toDateString()}
                          </p>
                        )}
                      </div>
                      <button
                        style={{ ...s.btn, padding: "6px 14px", fontSize: 12, background: "#6366f1" }}
                        onClick={() => handleSelectActivity(a)}
                      >
                        Submissions
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={s.hint}>No activities created yet.</p>
              )}
            </section>
          )}

          {/* SUBMISSIONS VIEW */}
          {selectedActivity && (
            <section className="card">
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <button
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#6366f1", fontWeight: 600, fontSize: 14 }}
                  onClick={() => { setSelectedActivity(null); setSubmissions(null); }}
                >
                  ← Back
                </button>
                <h2 style={{ margin: 0 }}>{selectedActivity.title} — Submissions</h2>
              </div>

              {submissions === null && <p style={s.hint}>Loading submissions...</p>}
              {submissions?.length === 0 && <p style={s.hint}>No submissions yet.</p>}
              {submissions?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {submissions.map((sub) => (
                    <div key={sub.id} style={{ ...s.actCard, flexDirection: "column", alignItems: "stretch", gap: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <strong>{sub.learner_name || `Learner #${sub.learner_id}`}</strong>
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>
                          {sub.submitted_at ? new Date(sub.submitted_at).toDateString() : ""}
                        </span>
                      </div>
                      {sub.content && (
                        <p style={{ fontSize: 14, color: "#475569", margin: 0, whiteSpace: "pre-wrap" }}>{sub.content}</p>
                      )}
                      {sub.grade != null && (
                        <p style={{ fontSize: 13, color: "#166534", fontWeight: 600 }}>Grade: {sub.grade} — {sub.feedback}</p>
                      )}
                      {sub.grade == null && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <input
                            style={{ ...s.input, width: 80, flex: "none" }}
                            placeholder="Grade"
                            value={gradeInputs[sub.id]?.grade || ""}
                            onChange={(e) => setGradeInputs({ ...gradeInputs, [sub.id]: { ...gradeInputs[sub.id], grade: e.target.value } })}
                          />
                          <input
                            style={{ ...s.input, flex: 1, minWidth: 150 }}
                            placeholder="Feedback (optional)"
                            value={gradeInputs[sub.id]?.feedback || ""}
                            onChange={(e) => setGradeInputs({ ...gradeInputs, [sub.id]: { ...gradeInputs[sub.id], feedback: e.target.value } })}
                          />
                          <button style={{ ...s.btn, padding: "8px 16px", fontSize: 13 }} onClick={() => handleGrade(sub.id)}>
                            Grade
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {gradeMsg && <p style={{ fontSize: 13 }}>{gradeMsg}</p>}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

const s = {
  hint:     { fontSize: 13, color: "#64748b", margin: "0 0 10px" },
  btn:      { padding: "9px 20px", background: "#1e293b", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  input:    { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 },
  actCard:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" },
  statBox:  { display: "flex", flexDirection: "column", alignItems: "center", background: "#f8fafc", borderRadius: 12, padding: "16px 24px", minWidth: 80 },
  statNum:  { fontSize: 24, fontWeight: 800, color: "#1e293b" },
  statLabel:{ fontSize: 12, color: "#64748b", marginTop: 4 },
};

export default EducatorDashboard;
